import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Workload } from '../api/types';
import { SynthesisTable } from '../components/SynthesisTable';
import { CompiledSynthesisChart } from '../components/CompiledSynthesisChart';
import { SourceDiagramExportCard } from '../components/SourceDiagramExportCard';
import { CompiledExportToolbar } from '../components/CompiledExportToolbar';

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

interface Range {
  start: string;
  end: string;
}

/** Structure VOLONTAIREMENT identique à SynthesisPage (demande explicite d'homogénéisation) — voir sa docstring. */
export function WorkloadPage() {
  const { t } = useTranslation();
  const { folderId, role, userId } = useParams();
  const [data, setData] = useState<Workload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // null = plage par défaut (semaine précédente + mois qui arrive, calculée
  // côté serveur) ; définie = plage choisie explicitement par l'utilisateur,
  // reconduite tant qu'il ne clique pas sur Réinitialiser.
  const [appliedRange, setAppliedRange] = useState<Range | null>(null);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [view, setView] = useState<'table' | 'chart'>('table');

  const load = () => {
    if (!folderId || !role || !userId) return;
    setRefreshing(true);
    setError(null);
    const query = appliedRange ? `?start=${appliedRange.start}&end=${appliedRange.end}` : '';
    api
      .get<Workload>(`/folders/${folderId}/workload/${role}/${userId}${query}`)
      .then((d) => {
        setData(d);
        // Tant qu'aucune plage personnalisée n'est appliquée, les champs de
        // saisie reflètent la plage par défaut renvoyée par le serveur —
        // point de départ naturel si l'utilisateur veut ensuite l'ajuster.
        if (!appliedRange) {
          setStartInput(formatDate(d.windowStart));
          setEndInput(formatDate(d.windowEnd));
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setRefreshing(false));
  };

  useEffect(load, [folderId, role, userId, appliedRange]);

  if (error) return <p className="error-text">{error}</p>;
  if (!data) return <p>{t('common.loading')}</p>;

  const rangeInvalid = !startInput || !endInput || startInput > endInput;
  const title = `${data.user.firstName} ${data.user.lastName} — ${t(`synthesis.role.${data.role}`)} · ${data.folderName}`;

  return (
    <div>
      <Link to={`/folders/${folderId}`}>&larr; {t('diagram.backToFolder')}</Link>

      <div className="card diagram-toolbar" style={{ marginTop: 16, marginBottom: 4 }}>
        <strong className="diagram-name-display">{title}</strong>
        <button className="btn icon-btn" onClick={load} disabled={refreshing} title={t('synthesis.refresh') as string}>
          🔄
        </button>
      </div>

      <div className="toolbar" style={{ margin: '4px 0 4px', alignItems: 'center' }}>
        <label style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('synthesis.windowFrom')}</label>
        <input type="date" value={startInput} onChange={(e) => setStartInput(e.target.value)} />
        <label style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('synthesis.windowTo')}</label>
        <input type="date" value={endInput} onChange={(e) => setEndInput(e.target.value)} />
        <button
          className="btn"
          disabled={rangeInvalid}
          onClick={() => setAppliedRange({ start: startInput, end: endInput })}
        >
          {t('synthesis.applyRange')}
        </button>
        {appliedRange && (
          <button className="btn" onClick={() => setAppliedRange(null)}>
            {t('synthesis.resetRange')}
          </button>
        )}
      </div>
      <p className="diagram-last-saved">
        {t('synthesis.workloadWindow', { start: formatDate(data.windowStart), end: formatDate(data.windowEnd) })}
      </p>

      <div className="toolbar" style={{ marginBottom: 8 }}>
        <button className={`btn ${view === 'table' ? 'btn-primary' : ''}`} onClick={() => setView('table')}>
          {t('diagram.viewTable')}
        </button>
        <button className={`btn ${view === 'chart' ? 'btn-primary' : ''}`} onClick={() => setView('chart')}>
          {t('diagram.viewChart')}
        </button>
      </div>

      {view === 'chart' && (
        <div style={{ marginBottom: 24 }}>
          <CompiledExportToolbar groups={data.groups} title={title} />
          <CompiledSynthesisChart groups={data.groups} />
        </div>
      )}

      {data.groups.map((group) => (
        <div key={group.diagramId} style={{ marginBottom: 24 }}>
          <SourceDiagramExportCard diagramId={group.diagramId} diagramName={group.diagramName} />

          {view === 'table' &&
            (group.tasks.length === 0 ? (
              <p className="synthesis-empty">{t('synthesis.noTask')}</p>
            ) : (
              <SynthesisTable groups={[group]} hideGroupHeader />
            ))}
        </div>
      ))}
    </div>
  );
}
